import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { CartItem } from "../../interfaces/cartInterface";
import { fetchCart, addCartItem, removeCartItems } from "../../utils/api/apiGuest";
import { RootState } from "../store";

interface CartState {
  cartId: string | null;
  items: CartItem[];
  loading: boolean;
  error: string | null;
}

const initialState: CartState = {
  cartId: null,
  items: [],
  loading: false,
  error: null,
};

export const initializeCart = createAsyncThunk(
  "cart/initialize",
  async (cartIdFromStorage?: string) => {
    const res = await fetchCart(cartIdFromStorage);
    return { cartId: res.data?.cartId, items: res.data?.items };
  }
);

export const addToCart = createAsyncThunk(
  "cart/add",
  async ({ cartId, productId, qty }: { cartId: string; productId: string; qty: number }) => {
    await addCartItem(cartId, productId, qty);
    return { productId, qty };
  }
);

export const removeFromCart = createAsyncThunk(
  "cart/remove",
  async ({ cartId, productIds }: { cartId: string; productIds: string[] }) => {
    await removeCartItems(cartId, productIds);
    return productIds;
  }
);

export const selectCartItemCount = (state: RootState): number => {
  return state.cart.items.reduce((sum, item) => sum + item.qty, 0);
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCart: (state) => {
      state.items = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeCart.fulfilled, (state, action) => {
        state.cartId = action.payload.cartId;
        state.items = action.payload.items;
        localStorage.setItem("cartId", action.payload.cartId);
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        const existing = state.items.find(i => i.productId === action.payload.productId);
        if (existing) {
          existing.qty += action.payload.qty;
        } else {
          state.items.push({
            cartId: state.cartId!,
            productId: action.payload.productId,
            qty: action.payload.qty
          });
        }
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.items = state.items.filter(item => !action.payload.includes(item.productId));
      });
  },
});

export const { clearCart } = cartSlice.actions;
export default cartSlice.reducer;